/**
 * The frontend **REST login** helper. Turns a credential (the GM host key, or a player
 * slot+password) into a reclaim **token** by POSTing `POST /api/game/login`. The
 * `/ws/game` hello is token-only, so the client authenticates here first, then opens the
 * socket with the issued token.
 *
 * Returns a `Result` rather than throwing: a `401`/`409` is an *expected* failure the
 * caller maps onto the login UI's reject copy (`GameRejectReason`), not a bug.
 */
import {
  GAME_LOGIN_PATH,
  ok,
  err,
  httpOriginOf,
  type Result,
  type GameLoginRequest,
  type GameLoginResponse,
  type GameLoginErrorResponse,
  type GameRejectReason,
} from "@lancer-vtt/shared";

/** A REST login failure: the HTTP status, plus the typed reason when the body carried one. */
export type LoginFailure = {
  /** The HTTP status (`401`/`409`/…), or `0` when the endpoint was unreachable. */
  status: number;
  /** The typed reject reason from the error body, or `null` (network failure / non-JSON). */
  reason: GameRejectReason | null;
};

/**
 * Normalize a base into the HTTP(S) origin the login endpoint lives on. **The `"" ⇒ ""` guard is
 * the whole function** — everything below it is `httpOriginOf` in `shared/`, which the CLI's
 * `gameLogin.ts` also calls (the two were byte-identical copies before the try/catch audit's
 * Phase 3 deduped them). The guard stays here rather than moving into the shared helper because
 * it is a *frontend* fact: production serves this SPA from the backend's own origin, so `""` is a
 * correct same-origin relative path here and would be meaningless in the CLI. A ws/http(s)
 * base/URL (the integration suites pass their `ws://host:port/ws/game`) still maps to its http(s)
 * origin with the path stripped, so callers can hand over a ws URL unchanged.
 */
function httpOrigin(base: string): string {
  if (base === "") return "";
  return httpOriginOf(base);
}

/**
 * POST a credential to `POST /api/game/login`. `baseUrl` defaults to "" (same-origin, the
 * production path); tests pass their ws/http base (normalized via {@link httpOrigin}).
 * Resolves to `ok(GameLoginResponse)` with the minted token, or `err(LoginFailure)` on a
 * non-2xx / unreachable endpoint.
 */
export async function restLogin(
  credential: GameLoginRequest,
  baseUrl = "",
): Promise<Result<GameLoginResponse, LoginFailure>> {
  let res: Response;
  try {
    res = await fetch(`${httpOrigin(baseUrl)}${GAME_LOGIN_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(credential),
    });
  } catch {
    return err({ status: 0, reason: null });
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Partial<GameLoginErrorResponse>;
    return err({ status: res.status, reason: body.reason ?? null });
  }
  return ok((await res.json()) as GameLoginResponse);
}

/**
 * Test convenience: REST-login and return just the token, throwing on failure. Used by
 * the real-ws integration suites to mint the token they present in a token-only ws hello.
 */
export async function loginForToken(credential: GameLoginRequest, baseUrl = ""): Promise<string> {
  const out = await restLogin(credential, baseUrl);
  if (!out.ok) throw new Error(`login failed (status ${out.error.status}): ${out.error.reason ?? "unreachable"}`);
  return out.value.token;
}
